// feature-ticket-acceptance-required.ts
//
// Phase 06e R4 (dogfood F-27, spec E25): `acceptance` is optional in the
// ticket schema (pre-C1 tickets), so a plan could create a testcase ticket
// with none — and the RED suite then only covered edge cases, never the main
// behavior. ONE predicate, used at two layers:
// - verifier step 6f (result-verifier-contract-field-checks.ts) on the wire
//   `proposed_changes[]`, `create` only (the kind is in `content`; for an
//   `amend` the verifier does not know the target's kind) ⇒ `rejected` ⇒
//   retry with the reason in the E19 section;
// - applier (proposal-applier.ts), the last net for create AND amend, also on
//   the structured redirect path ⇒ `proposal_apply_rejected` ⇒ escalate.
//
// Only testcase/implement/test. `fix` is regression-born and not measured yet;
// review/decision/plan/survey carry no behavior contract of their own. R3
// (blocked-plan supersede) reuses ACCEPTANCE_REQUIRED_KINDS as "a real slice".
//
// No redaction here: `target` is worker-chosen, so the verifier reason caps it
// at 64 chars; secrets are masked when the reason is committed (reducer
// patterns + known-values) and again when the E19 section renders.

/** Ticket kinds that must carry ≥ 1 non-blank acceptance row. */
export const ACCEPTANCE_REQUIRED_KINDS = ["testcase", "implement", "test"] as const;

const TARGET_MAX_CHARS = 64;

export function isAcceptanceRequiredKind(kind: unknown): boolean {
  return (ACCEPTANCE_REQUIRED_KINDS as readonly unknown[]).includes(kind);
}

/**
 * The one predicate (create and amend): a problem description when `kind`
 * requires acceptance and `acceptance` is absent, empty, or has a row whose
 * `text` is blank; `undefined` otherwise. Reads defensively (wire input).
 */
export function featureTicketAcceptanceProblem(
  kind: unknown,
  acceptance: unknown,
): string | undefined {
  if (!isAcceptanceRequiredKind(kind)) return undefined;
  if (!Array.isArray(acceptance)) return "thiếu acceptance[]";
  if (acceptance.length === 0) return "acceptance[] rỗng";
  const blank = acceptance.findIndex((row) => {
    const text = (row as { text?: unknown } | null)?.text;
    return typeof text !== "string" || text.trim() === "";
  });
  return blank === -1 ? undefined : `acceptance[${blank}].text trắng`;
}

/** Verifier step 6f: first wire `create` of a testcase/implement/test ticket without acceptance. */
export function proposedChangesAcceptanceRejection(
  proposedChanges: readonly unknown[] | undefined,
): string | undefined {
  for (const [index, raw] of (proposedChanges ?? []).entries()) {
    const change = raw as { op?: unknown; target?: unknown; content?: unknown } | null;
    if (change?.op !== "create") continue;
    const content = (change.content ?? {}) as { kind?: unknown; acceptance?: unknown };
    if (featureTicketAcceptanceProblem(content.kind, content.acceptance) === undefined) continue;
    const target = String(change.target ?? "")
      .replace(/\s+/g, " ")
      .slice(0, TARGET_MAX_CHARS);
    return (
      `feature_ticket_without_acceptance: proposed_changes[${index}] (create ${target}, kind ${String(content.kind)}) ` +
      "thiếu acceptance[] — ticket testcase/implement/test phải có ≥ 1 điều kiện nghiệm thu theo hành vi (BLUEPRINT §Hành vi)"
    );
  }
  return undefined;
}

/**
 * Applier form, on the PARSED ticket (create: the new ticket; amend: the
 * merged ticket). An amend is judged only when its patch sets `acceptance` or
 * `kind` — an old ticket without acceptance may still get a title fix.
 */
export function ticketAcceptanceRejection(
  ticket: { id: string; kind: string; acceptance?: unknown },
  amendPatch?: object,
): string | undefined {
  if (amendPatch !== undefined && !("acceptance" in amendPatch) && !("kind" in amendPatch)) {
    return undefined;
  }
  const problem = featureTicketAcceptanceProblem(ticket.kind, ticket.acceptance);
  if (problem === undefined) return undefined;
  return `feature_ticket_without_acceptance: ticket ${ticket.id} (kind ${ticket.kind}) ${problem} — ticket testcase/implement/test phải có ≥ 1 điều kiện nghiệm thu theo hành vi`;
}
